<template>
  <div class="chat-view">
    <v-container class="chat-container">
      <v-row no-gutters class="fill-height chat-row">
        <!-- Linke Seitenleiste mit Kontakten/Chats -->
        <v-col
          cols="12"
          md="4"
          lg="3"
          class="chat-sidebar"
          :class="{ 'd-none': isMobile && selectedContact }"
        >
          <v-card class="sidebar-card fill-height">
            <v-card-title class="d-flex align-center">
              <div class="d-flex align-center justify-space-between w-100">
                <span class="title-text">Chats</span>
                <div>
                  <v-btn
                    icon="mdi-magnify"
                    variant="text"
                    density="comfortable"
                    @click="showSearchDialog = true"
                    class="action-btn"
                  ></v-btn>
                  <v-btn
                    icon="mdi-plus"
                    variant="text"
                    density="comfortable"
                    @click="showNewChatDialog = true"
                    class="action-btn"
                  ></v-btn>
                </div>
              </div>
            </v-card-title>
            <v-divider class="card-divider"></v-divider>

            <!-- Eigener Status-Bereich -->
            <div class="own-status-container pa-2">
              <v-badge
                :color="getStatusColor(authStore.currentStatus)"
                dot
                bordered
                location="bottom end"
                class="mr-2"
              >
                <v-avatar size="36" color="primary">
                  <span>{{
                    getInitials({
                      name:
                        authStore.user?.vorname +
                        " " +
                        authStore.user?.nachname,
                    })
                  }}</span>
                </v-avatar>
              </v-badge>
              <div class="user-status-details">
                <div class="user-display-name">
                  {{ authStore.user?.vorname }} {{ authStore.user?.nachname }}
                </div>
                <div class="user-current-status">
                  <span :class="`status-text ${authStore.currentStatus}`">
                    {{
                      authStore.currentStatusMessage ||
                      getStatusText(authStore.currentStatus)
                    }}
                  </span>
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-btn
                icon="mdi-pencil-outline"
                variant="text"
                size="small"
                @click="openStatusMenu"
                class="action-btn"
              ></v-btn>
            </div>

            <v-divider class="divider-thin"></v-divider>

            <!-- Filter-Tabs -->
            <v-tabs
              v-model="activeTab"
              bg-color="transparent"
              class="chat-tabs"
            >
              <v-tab value="chats">Direkt</v-tab>
              <v-tab value="groups">Gruppen</v-tab>
              <v-tab value="all">Alle</v-tab>
            </v-tabs>

            <!-- Sucheingabe -->
            <div class="search-container pa-3">
              <v-text-field
                v-model="searchQuery"
                prepend-inner-icon="mdi-magnify"
                placeholder="Suche..."
                variant="outlined"
                density="compact"
                hide-details
                class="search-field"
                bg-color="rgba(255, 255, 255, 0.05)"
              ></v-text-field>
            </div>

            <!-- Kontaktliste -->
            <v-list class="contact-list">
              <div
                v-if="loading"
                class="d-flex justify-center align-center pa-4"
              >
                <v-progress-circular
                  indeterminate
                  color="success"
                ></v-progress-circular>
              </div>

              <template v-else-if="filteredContacts.length > 0">
                <v-list-item
                  v-for="contact in filteredContacts"
                  :key="contact.id"
                  :active="selectedContact?.id === contact.id"
                  @click="selectContact(contact)"
                  class="contact-item"
                  :class="{ unread: contact.unread }"
                >
                  <template v-slot:prepend>
                    <v-badge
                      :color="contact.online ? 'success' : 'grey'"
                      dot
                      location="bottom end"
                      offset-x="3"
                      offset-y="3"
                    >
                      <v-avatar size="36" :color="getAvatarColor(contact)">
                        <span v-if="!contact.avatar" class="text-h6">{{
                          getInitials(contact)
                        }}</span>
                        <v-img v-else :src="contact.avatar"></v-img>
                      </v-avatar>
                    </v-badge>
                  </template>

                  <v-list-item-title class="contact-name">{{
                    contact.name
                  }}</v-list-item-title>
                  <v-list-item-subtitle
                    v-if="
                      contact.custom_status_message &&
                      contact.user_status === 'custom'
                    "
                    class="custom-status-message"
                  >
                    {{ contact.custom_status_message }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle
                    v-else-if="
                      contact.user_status && contact.user_status !== 'online'
                    "
                    class="status-text"
                    :class="contact.user_status"
                  >
                    {{ getStatusText(contact.user_status) }}
                  </v-list-item-subtitle>

                  <template v-slot:append>
                    <div class="d-flex flex-column align-end">
                      <span class="timestamp" v-if="contact.timestamp">{{
                        formatTimestamp(contact.timestamp)
                      }}</span>
                      <v-badge
                        v-if="contact.unreadCount"
                        :content="contact.unreadCount"
                        color="success"
                        class="mt-1"
                      ></v-badge>
                    </div>
                  </template>
                </v-list-item>
              </template>

              <div v-else class="no-results pa-4 text-center">
                <v-icon size="40" class="mb-2">mdi-chat-remove</v-icon>
                <p class="text-body-2 text-medium-emphasis">
                  Keine Chats gefunden
                </p>
              </div>
            </v-list>
          </v-card>
        </v-col>

        <!-- Trennlinie für Desktop-Ansicht -->
        <div class="chat-divider d-none d-md-block"></div>

        <!-- Hauptchat-Bereich -->
        <v-col cols="12" md="8" lg="9" class="chat-main">
          <template v-if="selectedContact">
            <v-card class="chat-card fill-height d-flex flex-column">
              <!-- Chat-Header mit Funktionalität -->
              <v-card-title class="d-flex align-center">
                <div class="d-flex align-center">
                  <v-btn
                    icon="mdi-arrow-left"
                    variant="text"
                    @click="goBackToContacts"
                    class="mobile-back-btn mr-2 action-btn"
                    v-if="isMobile"
                  ></v-btn>
                  <v-avatar
                    size="42"
                    :color="getAvatarColor(selectedContact)"
                    class="mr-3"
                  >
                    <span v-if="!selectedContact.avatar" class="text-h6">{{
                      getInitials(selectedContact)
                    }}</span>
                    <v-img v-else :src="selectedContact.avatar"></v-img>
                  </v-avatar>

                  <div>
                    <div class="d-flex align-center">
                      <span class="font-weight-medium chat-contact-name">{{
                        selectedContact.name
                      }}</span>
                      <!-- Status-Chip hinzufügen -->
                      <v-chip
                        v-if="selectedContact.online"
                        color="success"
                        size="x-small"
                        class="ml-2"
                        variant="outlined"
                        >Online</v-chip
                      >

                      <!-- Neuer Status-Chip mit dynamischer Farbe -->
                      <v-chip
                        v-if="
                          selectedContact.user_status &&
                          selectedContact.user_status !== 'online'
                        "
                        :color="getStatusColor(selectedContact.user_status)"
                        size="x-small"
                        class="ml-2"
                        variant="outlined"
                        >{{
                          getStatusText(selectedContact.user_status)
                        }}</v-chip
                      >
                    </div>
                    <div
                      class="text-caption text-medium-emphasis typing-indicator"
                      v-if="selectedContact.isTyping"
                    >
                      schreibt...
                    </div>
                    <!-- Custom-Status-Nachricht anzeigen, falls vorhanden -->
                    <div
                      v-else-if="selectedContact.custom_status_message"
                      class="text-caption custom-status-message"
                    >
                      {{ selectedContact.custom_status_message }}
                    </div>
                  </div>
                </div>
                <v-spacer></v-spacer>
                <div class="header-actions">
                  <v-btn
                    icon="mdi-information-outline"
                    variant="text"
                    density="comfortable"
                    @click="showContactInfo"
                    class="action-btn"
                  ></v-btn>
                  <v-menu location="bottom end" :close-on-content-click="true">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        icon="mdi-dots-vertical"
                        variant="text"
                        density="comfortable"
                        v-bind="props"
                        class="action-btn"
                      ></v-btn>
                    </template>
                    <v-list class="chat-options-menu">
                      <v-list-item @click="clearChat">
                        <v-list-item-title>Chat leeren</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="blockContact"
                        v-if="selectedContact.type === 'user'"
                      >
                        <v-list-item-title>Blockieren</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="leaveGroup"
                        v-if="selectedContact.type === 'group'"
                      >
                        <v-list-item-title>Gruppe verlassen</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="exportChat">
                        <v-list-item-title>Chat exportieren</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-card-title>

              <v-divider class="divider-thin"></v-divider>

              <!-- Chat-Nachrichten -->
              <v-card-text class="chat-messages" ref="messagesContainer">
                <div
                  v-if="loading"
                  class="d-flex justify-center align-center h-100"
                >
                  <v-progress-circular
                    indeterminate
                    color="success"
                  ></v-progress-circular>
                </div>

                <template v-else-if="messages.length > 0">
                  <div
                    v-for="(message, index) in messages"
                    :key="message.id"
                    class="message-item"
                    :class="{
                      'own-message': message.sender_id === currentUser.id,
                      'other-message': message.sender_id !== currentUser.id,
                      grouped: isGroupedMessage(message, index),
                    }"
                  >
                    <div class="message-bubble">
                      <!-- Absendername - nur bei fremden Nachrichten und wenn nicht gruppiert -->
                      <div
                        v-if="
                          message.sender_id !== currentUser.id &&
                          !isGroupedMessage(message, index)
                        "
                        class="message-sender"
                      >
                        {{ getMessageSender(message)?.name || "Unbekannt" }}
                      </div>

                      <!-- Nachrichtentext -->
                      <div
                        class="message-text"
                        v-html="formatMessageText(message.message)"
                      ></div>

                      <!-- Zeitstempel und Gelesen-Status in einer Zeile -->
                      <div class="message-info">
                        <span class="message-time">{{
                          formatMessageTime(message.created_at)
                        }}</span>
                        <v-icon
                          v-if="message.sender_id === currentUser.id"
                          :color="message.read_status ? 'success' : 'grey'"
                          size="x-small"
                          class="ml-1"
                        >
                          {{
                            message.read_status ? "mdi-check-all" : "mdi-check"
                          }}
                        </v-icon>
                      </div>
                    </div>
                  </div>
                </template>

                <div
                  v-else
                  class="no-messages d-flex flex-column justify-center align-center h-100"
                >
                  <v-icon size="64" color="grey" class="mb-3"
                    >mdi-chat-outline</v-icon
                  >
                  <h3 class="text-h6 text-medium-emphasis">
                    Keine Nachrichten
                  </h3>
                  <p class="text-body-2 text-medium-emphasis">
                    Beginne eine Unterhaltung mit {{ selectedContact.name }}
                  </p>
                </div>
              </v-card-text>

              <!-- Chat-Eingabe -->
              <v-card-actions class="chat-input">
                <v-menu
                  v-model="showEmojiPicker"
                  :close-on-content-click="false"
                  location="top"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon="mdi-emoticon-outline"
                      variant="text"
                      v-bind="props"
                      class="emoji-btn action-btn"
                    ></v-btn>
                  </template>
                  <v-card class="emoji-picker">
                    <v-card-text>
                      <div class="emoji-grid">
                        <button
                          v-for="emoji in emojiList"
                          :key="emoji"
                          @click="insertEmoji(emoji)"
                          class="emoji-btn"
                        >
                          {{ emoji }}
                        </button>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>

                <v-btn
                  icon="mdi-paperclip"
                  variant="text"
                  @click="openFileUpload"
                  class="action-btn"
                ></v-btn>

                <v-textarea
                  v-model="newMessage"
                  placeholder="Nachricht schreiben..."
                  variant="outlined"
                  rows="1"
                  auto-grow
                  hide-details
                  density="comfortable"
                  class="chat-input-field mx-2"
                  @keydown.enter.prevent="sendMessage"
                  @input="handleTyping"
                ></v-textarea>

                <v-btn
                  icon="mdi-send"
                  variant="text"
                  color="success"
                  :disabled="!newMessage.trim()"
                  @click="sendMessage"
                  class="send-btn"
                ></v-btn>
              </v-card-actions>
            </v-card>
          </template>

          <!-- Placeholder wenn kein Chat ausgewählt ist -->
          <v-card
            v-else
            class="chat-card fill-height d-flex flex-column justify-center align-center"
          >
            <v-icon size="80" color="grey" class="mb-4"
              >mdi-chat-processing-outline</v-icon
            >
            <h2 class="text-h5 text-medium-emphasis mb-2">
              Willkommen im Live-Chat
            </h2>
            <p class="text-body-1 text-medium-emphasis text-center mx-4">
              Wähle einen Kontakt oder eine Gruppe aus der Liste, um eine
              Unterhaltung zu beginnen.
            </p>
            <v-btn
              prepend-icon="mdi-account-plus"
              variant="outlined"
              color="success"
              class="mt-4"
              @click="showNewChatDialog = true"
            >
              Neuen Chat starten
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Kontakt-Info-Dialog -->
    <v-dialog v-model="showContactInfoDialog" max-width="400">
      <v-card class="contact-info-dialog dialog-card">
        <v-card-title class="info-header dialog-title">
          <span>Kontaktinformationen</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="showContactInfoDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-card-text v-if="selectedContact" class="dialog-content">
          <div class="contact-info-content">
            <v-avatar
              size="80"
              :color="getAvatarColor(selectedContact)"
              class="mb-4"
            >
              <span v-if="!selectedContact.avatar" class="text-h4">{{
                getInitials(selectedContact)
              }}</span>
              <v-img v-else :src="selectedContact.avatar"></v-img>
            </v-avatar>

            <h2 class="text-h5 mb-2">{{ selectedContact.name }}</h2>

            <!-- Status-Badge für Kontaktinformationen -->
            <v-chip
              :color="getStatusColor(selectedContact.user_status || 'offline')"
              class="mb-4"
              size="small"
            >
              {{ getStatusText(selectedContact.user_status || "offline") }}
            </v-chip>

            <!-- Benutzerdefinierte Status-Nachricht -->
            <div
              class="info-item custom-status"
              v-if="selectedContact.custom_status_message"
            >
              <v-icon class="mr-2" color="info"
                >mdi-message-text-outline</v-icon
              >
              <span>{{ selectedContact.custom_status_message }}</span>
            </div>

            <div class="info-item">
              <v-icon class="mr-2">mdi-account</v-icon>
              <span>{{
                selectedContact.type === "user" ? "Benutzer" : "Gruppe"
              }}</span>
            </div>

            <div class="info-item" v-if="selectedContact.abteilung">
              <v-icon class="mr-2">mdi-office-building</v-icon>
              <span>{{ selectedContact.abteilung }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Gruppenmitglieder-Dialog -->
    <v-dialog v-model="showGroupMembersDialog" max-width="500">
      <v-card class="group-members-dialog dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Gruppenmitglieder ({{ groupMembers.length }})</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="showGroupMembersDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content">
          <div v-if="loadingGroupMembers" class="d-flex justify-center my-4">
            <v-progress-circular
              indeterminate
              color="success"
            ></v-progress-circular>
          </div>

          <div v-else-if="groupMembers.length === 0" class="text-center my-4">
            Keine Mitglieder gefunden
          </div>

          <v-list v-else class="member-list">
            <v-list-item
              v-for="member in groupMembers"
              :key="member.id"
              class="member-item"
            >
              <template v-slot:prepend>
                <v-avatar size="40" :color="getAvatarColor(member)">
                  <span v-if="member.vorname && member.nachname">
                    {{
                      getInitials({
                        name: member.vorname + " " + member.nachname,
                      })
                    }}
                  </span>
                  <v-icon v-else>mdi-account</v-icon>
                </v-avatar>
              </template>

              <v-list-item-title>
                {{ member.vorname }} {{ member.nachname }}
                <v-chip
                  v-if="member.is_admin"
                  size="x-small"
                  color="success"
                  class="ml-2"
                  >Admin</v-chip
                >
                <v-chip
                  v-if="member.id === currentUser.id"
                  size="x-small"
                  color="success"
                  class="ml-2"
                  >Sie</v-chip
                >
              </v-list-item-title>
              <v-list-item-subtitle>
                <v-icon
                  :color="getStatusColor(member.user_status || 'offline')"
                  size="12"
                  class="mr-1"
                  >mdi-circle</v-icon
                >
                {{ getStatusText(member.user_status || "offline") }}
              </v-list-item-subtitle>

              <template
                v-slot:append
                v-if="isCurrentUserAdmin && member.id !== currentUser.id"
              >
                <v-menu location="bottom end">
                  <template v-slot:activator="{ props }">
                    <v-btn icon size="small" v-bind="props" class="action-btn">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list density="compact" class="context-menu">
                    <v-list-item
                      @click="toggleMemberAdmin(member)"
                      v-if="isCurrentUserAdmin"
                    >
                      <template v-slot:prepend>
                        <v-icon
                          size="small"
                          :color="member.is_admin ? 'error' : 'success'"
                        >
                          {{ member.is_admin ? "mdi-crown-off" : "mdi-crown" }}
                        </v-icon>
                      </template>
                      <v-list-item-title>
                        {{
                          member.is_admin
                            ? "Admin-Rechte entziehen"
                            : "Zum Admin ernennen"
                        }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="confirmRemoveMember(member)">
                      <template v-slot:prepend>
                        <v-icon size="small" color="error"
                          >mdi-account-remove</v-icon
                        >
                      </template>
                      <v-list-item-title
                        >Aus Gruppe entfernen</v-list-item-title
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-list-item>
          </v-list>

          <v-divider class="divider-thin my-4"></v-divider>

          <div class="d-flex justify-space-between">
            <v-btn
              color="error"
              variant="outlined"
              @click="confirmLeaveGroup"
              :loading="leavingGroup"
            >
              Gruppe verlassen
            </v-btn>

            <v-btn
              v-if="isCurrentUserAdmin"
              color="success"
              variant="outlined"
              @click="showAddMembersDialog = true"
            >
              Mitglieder hinzufügen
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialog zum Entfernen eines Mitglieds -->
    <v-dialog v-model="showRemoveMemberDialog" max-width="400">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Mitglied entfernen</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showRemoveMemberDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content">
          <v-alert
            type="warning"
            variant="tonal"
            density="compact"
            icon="mdi-alert-circle"
            class="mb-4"
          >
            Sind Sie sicher, dass Sie {{ memberToRemove?.vorname }}
            {{ memberToRemove?.nachname }} aus der Gruppe entfernen möchten?
          </v-alert>
          <p class="text-body-2 text-medium-emphasis mt-2">
            Das Mitglied wird informiert und kann der Gruppe nicht mehr
            beitreten, ohne erneut eingeladen zu werden.
          </p>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showRemoveMemberDialog = false"
            class="mr-2"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="removeMember"
            :loading="removingMember"
            elevation="1"
          >
            Entfernen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialog zum Verlassen der Gruppe -->
    <v-dialog v-model="showLeaveGroupDialog" max-width="400">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Gruppe verlassen</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showLeaveGroupDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content">
          <v-alert
            type="warning"
            variant="tonal"
            density="compact"
            icon="mdi-alert-circle"
            class="mb-4"
          >
            Sind Sie sicher, dass Sie die Gruppe "{{ selectedContact?.name }}"
            verlassen möchten?
          </v-alert>

          <div v-if="isCurrentUserAdmin" class="mt-4">
            <v-alert
              type="info"
              variant="tonal"
              density="compact"
              icon="mdi-information"
              color="info"
            >
              Als Admin wird Ihre Rolle an ein anderes Mitglied übertragen.
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showLeaveGroupDialog = false"
            class="mr-2"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="leaveGroup"
            :loading="leavingGroup"
            elevation="1"
          >
            Verlassen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog zum Hinzufügen neuer Mitglieder -->
    <v-dialog v-model="showAddMembersDialog" max-width="500">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Mitglieder hinzufügen</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showAddMembersDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <v-text-field
            v-model="userSearchQuery"
            label="Benutzer suchen"
            prepend-inner-icon="mdi-magnify"
            clearable
            variant="outlined"
            density="comfortable"
            class="mb-4 search-field"
            hide-details
          ></v-text-field>

          <div v-if="loadingUsers" class="d-flex justify-center my-4">
            <v-progress-circular
              indeterminate
              color="success"
            ></v-progress-circular>
          </div>

          <v-list
            v-else
            select-strategy="multiple"
            v-model="selectedNewMembers"
            class="member-selection-list"
          >
            <v-list-item
              v-for="user in filteredAddableUsers"
              :key="user.id"
              :value="user.id"
              :disabled="isUserInGroup(user.id)"
              class="member-select-item"
            >
              <template v-slot:prepend>
                <v-checkbox-btn
                  v-model="selectedNewMembers"
                  :value="user.id"
                  color="success"
                  density="comfortable"
                ></v-checkbox-btn>

                <v-avatar
                  size="36"
                  :color="
                    getAvatarColor({ name: user.vorname + ' ' + user.nachname })
                  "
                  class="ml-2"
                >
                  {{
                    getInitials({ name: user.vorname + " " + user.nachname })
                  }}
                </v-avatar>
              </template>

              <v-list-item-title
                >{{ user.vorname }} {{ user.nachname }}</v-list-item-title
              >
              <v-list-item-subtitle>
                <v-icon
                  :color="getStatusColor(user.user_status || 'offline')"
                  size="12"
                  class="mr-1"
                  >mdi-circle</v-icon
                >
                {{ user.abteilung || "Keine Abteilung" }}
              </v-list-item-subtitle>

              <template v-slot:append v-if="isUserInGroup(user.id)">
                <v-chip size="small" color="grey" variant="outlined"
                  >Bereits Mitglied</v-chip
                >
              </template>
            </v-list-item>
          </v-list>

          <div
            v-if="filteredAddableUsers.length === 0 && !loadingUsers"
            class="text-center my-6"
          >
            <v-icon size="40" color="grey" class="mb-2"
              >mdi-account-search</v-icon
            >
            <p class="text-body-2 text-medium-emphasis">
              Keine passenden Benutzer gefunden
            </p>
          </div>
        </v-card-text>

        <v-divider class="divider-thin"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <span v-if="selectedNewMembers.length > 0" class="text-caption">
            {{ selectedNewMembers.length }} Benutzer ausgewählt
          </span>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showAddMembersDialog = false"
            class="mr-2"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            @click="addMembersToGroup"
            :disabled="selectedNewMembers.length === 0"
            :loading="addingMembers"
            elevation="1"
          >
            Hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Datei-Upload-Dialog -->
    <v-dialog v-model="showFileUploadDialog" max-width="500">
      <v-card class="dialog-card file-upload-dialog">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Datei senden</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showFileUploadDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <p class="text-body-2 text-medium-emphasis mb-4">
            Wählen Sie eine Datei aus, die Sie an
            {{ selectedContact?.name }} senden möchten.
          </p>

          <v-file-input
            v-model="uploadFile"
            label="Datei auswählen"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            show-size
            counter
            variant="outlined"
            prepend-icon="mdi-file-upload"
            density="comfortable"
            class="file-input"
            :hint="
              uploadFile
                ? 'Dateigröße: ' + formatFileSize(uploadFile.size)
                : 'Max. 10 MB'
            "
          ></v-file-input>

          <div class="supported-formats mt-4">
            <v-chip
              size="small"
              color="success"
              variant="outlined"
              class="mr-1 mb-1"
              >Bilder</v-chip
            >
            <v-chip
              size="small"
              color="success"
              variant="outlined"
              class="mr-1 mb-1"
              >PDF</v-chip
            >
            <v-chip
              size="small"
              color="success"
              variant="outlined"
              class="mr-1 mb-1"
              >Word</v-chip
            >
            <v-chip
              size="small"
              color="success"
              variant="outlined"
              class="mr-1 mb-1"
              >Excel</v-chip
            >
          </div>
        </v-card-text>

        <v-divider class="divider-thin"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showFileUploadDialog = false"
            class="mr-2"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            :disabled="!uploadFile"
            @click="sendFile"
            :loading="uploading"
            prepend-icon="mdi-send"
            elevation="1"
          >
            Senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog für neue Konversation -->
    <v-dialog v-model="showNewChatDialog" max-width="500">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Neuen Chat starten</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showNewChatDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <v-tabs v-model="newChatTab" class="chat-tabs mb-4">
            <v-tab value="user">Benutzer</v-tab>
            <v-tab value="group">Gruppe</v-tab>
          </v-tabs>

          <v-window v-model="newChatTab">
            <v-window-item value="user">
              <v-text-field
                v-model="userSearchQuery"
                label="Suche nach Benutzern"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="comfortable"
                class="search-field mb-4"
                hide-details
              ></v-text-field>

              <v-list class="user-search-list mt-2">
                <v-list-item
                  v-for="user in filteredUsers"
                  :key="user.id"
                  @click="startChat(user)"
                  class="search-user-item"
                >
                  <template v-slot:prepend>
                    <v-avatar size="36" :color="getAvatarColor(user)">
                      <span v-if="!user.avatar" class="text-body-1">{{
                        getInitials(user)
                      }}</span>
                      <v-img v-else :src="user.avatar"></v-img>
                    </v-avatar>
                  </template>

                  <v-list-item-title>{{ user.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="user.abteilung">{{
                    user.abteilung
                  }}</v-list-item-subtitle>

                  <template v-slot:append>
                    <v-icon color="success" size="small"
                      >mdi-chat-outline</v-icon
                    >
                  </template>
                </v-list-item>

                <div
                  v-if="filteredUsers.length === 0"
                  class="no-results pa-4 text-center"
                >
                  <v-icon size="40" color="grey" class="mb-2"
                    >mdi-account-search</v-icon
                  >
                  <p class="text-body-2 text-medium-emphasis">
                    Keine Benutzer gefunden
                  </p>
                </div>
              </v-list>
            </v-window-item>

            <v-window-item value="group">
              <v-text-field
                v-model="newGroup.name"
                label="Gruppenname"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-account-group"
                class="mb-4 group-name-field"
                hide-details
              ></v-text-field>

              <v-textarea
                v-model="newGroup.description"
                label="Beschreibung (optional)"
                variant="outlined"
                rows="2"
                density="comfortable"
                prepend-inner-icon="mdi-text-box-outline"
                class="mb-4 group-description-field"
                hide-details
              ></v-textarea>

              <v-autocomplete
                v-model="newGroup.members"
                :items="allUsers"
                item-title="name"
                item-value="id"
                label="Mitglieder hinzufügen"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-account-plus"
                class="members-selection"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    class="ma-1"
                    color="success"
                    variant="outlined"
                  >
                    {{ item.raw.name }}
                  </v-chip>
                  <span
                    v-else-if="index === 3"
                    class="text-caption text-success text--darken-1"
                  >
                    (+{{ newGroup.members.length - 3 }} weitere)
                  </span>
                </template>

                <template v-slot:item="{ props, item }">
                  <v-list-item
                    v-bind="props"
                    :title="item.raw.name"
                    :subtitle="item.raw.abteilung || ''"
                    prepend-avatar
                  >
                    <template v-slot:prepend>
                      <v-avatar size="32" :color="getAvatarColor(item.raw)">
                        {{ getInitials(item.raw) }}
                      </v-avatar>
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <div v-if="newGroup.members.length > 0" class="mt-4">
                <v-chip color="success" size="small">
                  {{ newGroup.members.length }} Mitglieder ausgewählt
                </v-chip>
              </div>
            </v-window-item>
          </v-window>
        </v-card-text>

        <v-divider class="divider-thin"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showNewChatDialog = false" class="mr-2">
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            :disabled="!canCreateChat"
            @click="createChat"
            elevation="1"
          >
            Chat erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog für die Suche -->
    <v-dialog v-model="showSearchDialog" max-width="500">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <span>Chat-Nachrichten durchsuchen</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showSearchDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <v-text-field
            v-model="globalSearchQuery"
            label="Suchbegriff eingeben"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="comfortable"
            @keydown.enter="searchMessages"
            class="search-field mb-4"
            hide-details
          ></v-text-field>

          <v-progress-linear
            v-if="loading"
            indeterminate
            color="success"
            class="mb-4"
          ></v-progress-linear>

          <v-list
            v-if="searchResults.length > 0"
            class="search-results-list mt-3"
          >
            <v-list-item
              v-for="result in searchResults"
              :key="result.id"
              @click="goToSearchResult(result)"
              class="search-result-item"
            >
              <template v-slot:prepend>
                <v-avatar size="36" :color="getAvatarColor(result.contact)">
                  <span v-if="!result.contact.avatar" class="text-body-1">{{
                    getInitials(result.contact)
                  }}</span>
                  <v-img v-else :src="result.contact.avatar"></v-img>
                </v-avatar>
              </template>

              <v-list-item-title>{{ result.contact.name }}</v-list-item-title>
              <v-list-item-subtitle class="text-truncate">
                <span class="search-result-text">{{ result.message }}</span>
              </v-list-item-subtitle>

              <template v-slot:append>
                <span class="text-caption timestamp">{{
                  formatTimestamp(result.timestamp)
                }}</span>
              </template>
            </v-list-item>
          </v-list>

          <div v-else-if="hasSearched" class="no-results pa-4 text-center">
            <v-icon size="40" color="grey" class="mb-2">mdi-text-search</v-icon>
            <p class="text-body-2 text-medium-emphasis">
              Keine Ergebnisse gefunden
            </p>
            <p class="text-caption text-grey mt-2">
              Versuche es mit einem anderen Suchbegriff
            </p>
          </div>
        </v-card-text>

        <v-divider class="divider-thin"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showSearchDialog = false" class="mr-2">
            Schließen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            :disabled="!globalSearchQuery.trim()"
            @click="searchMessages"
            prepend-icon="mdi-magnify"
            elevation="1"
          >
            Suchen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Statusdialog -->
    <v-dialog v-model="showStatusDialog" max-width="320">
      <v-card class="status-dialog dialog-card">
        <v-card-title class="status-dialog-title dialog-title">
          Status setzen
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            size="small"
            variant="text"
            @click="showStatusDialog = false"
            class="action-btn"
          ></v-btn>
        </v-card-title>

        <v-divider class="divider-thin"></v-divider>

        <v-list class="status-options-list">
          <v-list-item
            v-for="status in statusOptions"
            :key="status.value"
            @click="setUserStatus(status.value)"
            :active="authStore.currentStatus === status.value"
            class="status-list-item"
          >
            <template v-slot:prepend>
              <v-icon :color="getStatusColor(status.value)">{{
                status.icon
              }}</v-icon>
            </template>
            <v-list-item-title>{{ status.label }}</v-list-item-title>
          </v-list-item>
        </v-list>

        <v-divider class="divider-thin"></v-divider>

        <!-- Benutzerdefinierter Status -->
        <div class="custom-status-section pa-4">
          <v-text-field
            v-model="customStatusMessage"
            label="Benutzerdefinierte Nachricht"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="authStore.currentStatus !== 'custom'"
            placeholder="Was machst du gerade?"
            prepend-inner-icon="mdi-message-text-outline"
            class="custom-status-field"
          ></v-text-field>

          <v-btn
            block
            color="success"
            class="mt-3"
            :disabled="
              authStore.currentStatus !== 'custom' || !customStatusMessage
            "
            @click="updateCustomStatus"
          >
            Nachricht speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useToast } from "vue-toastification";
import axios from "axios";
import { API_BASE_URL, WS_URL } from "@/config";
import DOMPurify from "dompurify";
import linkifyHtml from "linkify-html";
import ChatNotificationService from "@/services/ChatNotificationService";
import WebSocketService from "@/services/WebSocketService";
import { useRouter } from "vue-router";

// Router initialisieren
const router = useRouter();

// Store und Toast-Service initialisieren
const authStore = useAuthStore();
const toast = useToast();
const currentUser = computed(() => authStore.user);
const scrollObserver = ref(null);
const isTypingNow = ref(false);
const typingTimeout = ref(null);

// ======== Zustandsvariablen ========
// UI-Zustände
const loading = ref(false);
const activeTab = ref("chats");
const searchQuery = ref("");
const newMessage = ref("");
const globalSearchQuery = ref("");
const hasSearched = ref(false);
const messagesContainer = ref(null);

// Dialog-Zustände
const showNewChatDialog = ref(false);
const showSearchDialog = ref(false);
const newChatTab = ref("user");
const userSearchQuery = ref("");

// Daten-Zustände
const contacts = ref([]);
const messages = ref([]);
const searchResults = ref([]);
const allUsers = ref([]);
const selectedContact = ref(null);

// Gruppenchat-Formular
const newGroup = ref({
  name: "",
  description: "",
  members: [],
});

// Emoji-Picker
const showEmojiPicker = ref(false);
const emojiList = ref([
  "😀",
  "😃",
  "😄",
  "😁",
  "😆",
  "😅",
  "😂",
  "🤣",
  "😊",
  "😇",
  "🙂",
  "🙃",
  "😉",
  "😌",
  "😍",
  "🥰",
  "😘",
  "😗",
  "😙",
  "😚",
  "😋",
  "😛",
  "😝",
  "😜",
  "🤪",
  "🤨",
  "🧐",
  "🤓",
  "😎",
  "🤩",
  "👍",
  "👎",
  "❤️",
  "👋",
  "🙌",
  "👏",
  "🎉",
  "🔥",
  "💯",
  "✅",
]);

const insertEmoji = (emoji) => {
  newMessage.value += emoji;
  showEmojiPicker.value = false;
};

// Im script-Teil diese neuen Variablen hinzufügen:
const showGroupMembersDialog = ref(false);
const showLeaveGroupDialog = ref(false);
const showRemoveMemberDialog = ref(false);
const showAddMembersDialog = ref(false);
const groupMembers = ref([]);
const loadingGroupMembers = ref(false);
const memberToRemove = ref(null);
const removingMember = ref(false);
const removingMemberId = ref(null);
const leavingGroup = ref(false);
const selectedNewMembers = ref([]);
const addingMembers = ref(false);
const loadingUsers = ref(false);

// Berechnete Eigenschaften hinzufügen
const isCurrentUserAdmin = computed(() => {
  if (!selectedContact.value || selectedContact.value.type !== "group")
    return false;

  // Prüfe, ob der aktuelle Benutzer Admin der Gruppe ist
  const currentMember = groupMembers.value.find(
    (m) => m.id === currentUser.value.id
  );
  return currentMember && !!currentMember.is_admin;
});
// Methode hinzufügen, um zu prüfen, ob ein Benutzer bereits in der Gruppe ist
const isUserInGroup = (userId) => {
  return groupMembers.value.some((member) => member.id === userId);
};

// Filterung für hinzufügbare Benutzer
const filteredAddableUsers = computed(() => {
  if (!allUsers.value) return [];

  return allUsers.value.filter((user) => {
    const searchMatches =
      userSearchQuery.value === "" ||
      user.vorname
        .toLowerCase()
        .includes(userSearchQuery.value.toLowerCase()) ||
      user.nachname.toLowerCase().includes(userSearchQuery.value.toLowerCase());

    return searchMatches;
  });
});

// Methoden zur Gruppenverwaltung hinzufügen
const fetchGroupMembers = async () => {
  if (!selectedContact.value || selectedContact.value.type !== "group") return;

  loadingGroupMembers.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}chat.php`, {
      params: {
        action: "group_members",
        group_id: selectedContact.value.id,
      },
      withCredentials: true,
    });

    if (response.data.status === "success") {
      groupMembers.value = response.data.members || [];

      // Überprüfen, ob der aktuelle Benutzer Admin der Gruppe ist
      const currentMember = groupMembers.value.find(
        (m) => m.id === currentUser.value.id
      );
      if (currentMember) {
        // Aktualisiere die berechnete Eigenschaft auch manuell für reaktive Konsistenz
        isCurrentUserAdmin.value = !!currentMember.is_admin;
      }
    } else {
      toast.error(
        response.data.message || "Fehler beim Laden der Gruppenmitglieder"
      );
    }
  } catch (error) {
    console.error("Fehler beim Laden der Gruppenmitglieder:", error);
    toast.error("Fehler beim Laden der Gruppenmitglieder");
  } finally {
    loadingGroupMembers.value = false;
  }
};

const confirmRemoveMember = (member) => {
  memberToRemove.value = member;
  showRemoveMemberDialog.value = true;
};

const removeMember = async () => {
  if (!memberToRemove.value || !selectedContact.value) return;

  removingMember.value = true;
  removingMemberId.value = memberToRemove.value.id;

  try {
    const response = await axios.post(
      `${API_BASE_URL}chat.php?action=remove_member`,
      {
        group_id: selectedContact.value.id,
        user_id: memberToRemove.value.id,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        `${memberToRemove.value.vorname} ${memberToRemove.value.nachname} wurde aus der Gruppe entfernt`
      );

      // Mitglied aus der lokalen Liste entfernen
      groupMembers.value = groupMembers.value.filter(
        (m) => m.id !== memberToRemove.value.id
      );

      showRemoveMemberDialog.value = false;
    } else {
      toast.error(
        response.data.message || "Fehler beim Entfernen des Mitglieds"
      );
    }
  } catch (error) {
    console.error("Fehler beim Entfernen des Mitglieds:", error);
    toast.error("Fehler beim Entfernen des Mitglieds");
  } finally {
    removingMember.value = false;
    removingMemberId.value = null;
  }
};

const confirmLeaveGroup = () => {
  showLeaveGroupDialog.value = true;
};

const addMembersToGroup = async () => {
  if (!selectedContact.value || selectedNewMembers.value.length === 0) return;

  addingMembers.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}chat.php?action=add_members`,
      {
        group_id: selectedContact.value.id,
        user_ids: selectedNewMembers.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        `${selectedNewMembers.value.length} Mitglieder wurden hinzugefügt`
      );

      // Gruppenmitglieder neu laden
      await fetchGroupMembers();

      // Dialog schließen und Auswahl zurücksetzen
      showAddMembersDialog.value = false;
      selectedNewMembers.value = [];
    } else {
      toast.error(
        response.data.message || "Fehler beim Hinzufügen der Mitglieder"
      );
    }
  } catch (error) {
    console.error("Fehler beim Hinzufügen der Mitglieder:", error);
    toast.error("Fehler beim Hinzufügen der Mitglieder");
  } finally {
    addingMembers.value = false;
  }
};

// Kontakt-Info Dialog
const showContactInfoDialog = ref(false);

// Überarbeitete Methode für das Kontaktmenü
const showContactInfo = () => {
  if (selectedContact.value.type === "group") {
    fetchGroupMembers(); // Diese Funktion aktualisiert auch groupMembers.value
    showGroupMembersDialog.value = true;
  } else {
    showContactInfoDialog.value = true;
  }
};

const toggleMemberAdmin = async (member) => {
  if (!isCurrentUserAdmin.value || !selectedContact.value) return;

  const loadingText = member.is_admin
    ? "Entziehe Admin-Rechte..."
    : "Ernenne zum Admin...";

  const successText = member.is_admin
    ? `${member.vorname} ${member.nachname} ist kein Admin mehr`
    : `${member.vorname} ${member.nachname} ist jetzt Admin`;

  try {
    const response = await axios.post(
      `${API_BASE_URL}chat.php?action=toggle_admin`,
      {
        group_id: selectedContact.value.id,
        user_id: member.id,
        is_admin: member.is_admin ? 0 : 1,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      // Update lokales Mitglied
      member.is_admin = !member.is_admin;
      toast.success(successText);
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Admin-Status"
      );
    }
  } catch (error) {
    console.error("Fehler beim Ändern des Admin-Status:", error);
    toast.error("Fehler beim Ändern des Admin-Status");
  }
};

const statusOptions = [
  { label: "Online", value: "online", icon: "mdi-circle" },
  { label: "Abwesend", value: "away", icon: "mdi-clock-outline" },
  { label: "Beschäftigt", value: "busy", icon: "mdi-minus-circle" },
  { label: "Mobile", value: "mobile", icon: "mdi-cellphone" },
  { label: "Benutzerdefiniert", value: "custom", icon: "mdi-pencil" },
  { label: "Offline", value: "offline", icon: "mdi-circle-outline" },
];

const customStatusMessage = ref(authStore.currentStatusMessage || "");

const setUserStatus = async (status) => {
  if (status === "custom") {
    // Bei Auswahl von "Benutzerdefiniert" den Status ändern und Nachricht vorbereiten
    customStatusMessage.value = authStore.currentStatusMessage || "";
    await authStore.updateUserStatus("custom", customStatusMessage.value);
  } else {
    await authStore.updateUserStatus(status);
    showStatusDialog.value = false;
  }
};

const updateCustomStatus = async () => {
  if (customStatusMessage.value.trim()) {
    await authStore.updateUserStatus("custom", customStatusMessage.value);
    showStatusDialog.value = false;
  }
};

// Datei-Upload
const showFileUploadDialog = ref(false);
const uploadFile = ref(null);
const uploading = ref(false);

const openFileUpload = () => {
  showFileUploadDialog.value = true;
};

const sendFile = async () => {
  if (!uploadFile.value || !selectedContact.value) return;

  try {
    uploading.value = true;

    const formData = new FormData();
    formData.append("file", uploadFile.value);
    formData.append("recipient_id", selectedContact.value.id);
    formData.append("recipient_type", selectedContact.value.type || "user");

    const response = await axios.post(
      `${API_BASE_URL}chat_upload.php`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    if (response.data.status === "success") {
      showFileUploadDialog.value = false;
      uploadFile.value = null;
      toast.success("Datei wurde gesendet");
    } else {
      toast.error("Fehler beim Senden der Datei");
    }
  } catch (error) {
    console.error("Fehler beim Datei-Upload:", error);
    toast.error("Fehler beim Hochladen der Datei");
  } finally {
    uploading.value = false;
  }
};

// Menü-Funktionen
const clearChat = () => {
  // Dialog zur Bestätigung zeigen oder direkt ausführen
  if (
    confirm("Möchtest du wirklich alle Nachrichten in diesem Chat löschen?")
  ) {
    messages.value = [];
    toast.info("Chat wurde geleert");
  }
};

const blockContact = () => {
  // Blockier-Logik implementieren
  toast.info("Diese Funktion wird in Kürze verfügbar sein");
};

const leaveGroup = async () => {
  if (!selectedContact.value) return;

  leavingGroup.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}chat.php?action=leave_group`,
      {
        group_id: selectedContact.value.id,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        `Sie haben die Gruppe "${selectedContact.value.name}" verlassen`
      );

      // Gruppe aus der Kontaktliste entfernen
      contacts.value = contacts.value.filter(
        (c) => !(c.type === "group" && c.id === selectedContact.value.id)
      );

      // Dialog schließen und zur Kontaktliste zurückkehren
      showLeaveGroupDialog.value = false;
      showGroupMembersDialog.value = false;
      selectedContact.value = null;

      // Auf Mobilgeräten die Sidebar wieder einblenden
      if (isMobile.value) {
        showSidebar.value = true;
      }
    } else {
      toast.error(response.data.message || "Fehler beim Verlassen der Gruppe");
    }
  } catch (error) {
    console.error("Fehler beim Verlassen der Gruppe:", error);
    toast.error("Fehler beim Verlassen der Gruppe");
  } finally {
    leavingGroup.value = false;
  }
};

const exportChat = () => {
  // Chat-Export implementieren
  const chatHistory = messages.value
    .map((msg) => {
      const sender =
        msg.sender_id === currentUser.value.id
          ? "Du"
          : selectedContact.value.name;
      const time = formatMessageTime(msg.created_at);
      return `[${time}] ${sender}: ${msg.message}`;
    })
    .join("\n");

  // Erstelle temporäres Element zum Herunterladen
  const element = document.createElement("a");
  const file = new Blob([chatHistory], { type: "text/plain" });
  element.href = URL.createObjectURL(file);
  element.download = `Chat_mit_${selectedContact.value.name}_${
    new Date().toISOString().split("T")[0]
  }.txt`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);

  toast.success("Chat wurde exportiert");
};

// WebSocket-Verbindung
const chatSocket = ref(null);

// ======== Berechnete Eigenschaften ========
// Gefilterte Kontakte basierend auf Tab und Suchbegriff
const filteredContacts = computed(() => {
  const filtered = contacts.value.filter((contact) => {
    // Filtere nach Tab
    if (activeTab.value === "chats" && contact.type !== "user") return false;
    if (activeTab.value === "groups" && contact.type !== "group") return false;

    // Suche im Namen und letzter Nachricht
    const searchLower = searchQuery.value.toLowerCase();
    return (
      !searchQuery.value ||
      contact.name.toLowerCase().includes(searchLower) ||
      (contact.lastMessage &&
        contact.lastMessage.toLowerCase().includes(searchLower))
    );
  });

  // Sortiere: Ungelesene zuerst, dann nach letzter Nachrichtenzeit
  return filtered.sort((a, b) => {
    if (a.unreadCount && !b.unreadCount) return -1;
    if (!a.unreadCount && b.unreadCount) return 1;

    // Vergleiche Zeitstempel (neueste zuerst)
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeB - timeA;
  });
});

// Neue Funktion zum Senden des Tipp-Status
const sendTypingStatus = () => {
  if (!isTypingNow.value && selectedContact.value) {
    isTypingNow.value = true;

    // Sende Tipp-Status über WebSocket
    WebSocketService.send({
      type: "typing_status",
      recipient_id: selectedContact.value.id,
      recipient_type: selectedContact.value.type || "user",
      is_typing: true,
    });

    // Setze Timeout, um nach 3 Sekunden Inaktivität den Status zurückzusetzen
    clearTimeout(typingTimeout.value);
    typingTimeout.value = setTimeout(() => {
      isTypingNow.value = false;
      WebSocketService.send({
        type: "typing_status",
        recipient_id: selectedContact.value.id,
        recipient_type: selectedContact.value.type || "user",
        is_typing: false,
      });
    }, 3000);
  }
};

// Event-Handler für Texteingabefeld
const handleTyping = () => {
  sendTypingStatus();
};

// In onBeforeUnmount hinzufügen:
if (typingTimeout.value) {
  clearTimeout(typingTimeout.value);
}

// Gefilterte Benutzer für die Suche bei neuem Chat
const filteredUsers = computed(() => {
  if (!userSearchQuery.value) return allUsers.value;

  const query = userSearchQuery.value.toLowerCase();
  return allUsers.value.filter(
    (user) =>
      user.name.toLowerCase().includes(query) ||
      (user.abteilung && user.abteilung.toLowerCase().includes(query))
  );
});

// Prüfen, ob ein neuer Chat erstellt werden kann
const canCreateChat = computed(() => {
  if (newChatTab.value === "user") {
    // Ein Benutzer muss ausgewählt sein
    return (
      userSearchQuery.value.trim() !== "" && filteredUsers.value.length > 0
    );
  } else {
    // Gruppe braucht Namen und mindestens ein Mitglied
    return (
      newGroup.value.name.trim() !== "" && newGroup.value.members.length > 0
    );
  }
});

// ======== Methoden ========
// Initialisierung
const initChatApp = async () => {
  loading.value = true;
  try {
    await Promise.all([fetchContacts(), fetchAllUsers()]);
    await fetchContactsStatus();

    // WebSocket neu verbinden, um sicherzustellen, dass die Verbindung aktiv ist
    WebSocketService.connect();

    // Beim Initialisieren des Chats explizit einen Status-Update anfordern
    const statusResponse = await axios.get(`${API_BASE_URL}activeUsers.php?action=get_active_users`, {
      withCredentials: true,
    });

    // Überprüfen, ob der eigene Status korrekt angezeigt wird
    if (
      statusResponse.data.status === "success" &&
      Array.isArray(statusResponse.data.users)
    ) {
      const currentUserId = authStore.user?.id;
      const currentUserData = statusResponse.data.users.find(
        (u) => u.id === currentUserId
      );

      if (currentUserData && currentUserData.inactive_minutes > 0) {
        // Wenn inkorrekte Inaktivität angezeigt wird, expliziten Reset durchführen
        await axios.post(
          `${API_BASE_URL}updateActivity.php`,
          {
            action: "update",
            forceResetInactivity: true,
          },
          { withCredentials: true }
        );
      }
    }
  } catch (error) {
    console.error("Fehler beim Initialisieren des Chats:", error);
    toast.error("Der Chat konnte nicht initialisiert werden.");
  } finally {
    loading.value = false;
  }
};

// Neue Funktion: Status aller Kontakte abrufen
const fetchContactsStatus = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}activeUsers.php?action=get_active_users`, {
      withCredentials: true,
    });

    if (
      response.data.status === "success" &&
      Array.isArray(response.data.users)
    ) {
      // Aktualisiere die Status-Informationen aller Kontakte
      contacts.value.forEach((contact) => {
        // Finde den entsprechenden Benutzer in der aktiven Benutzerliste
        const activeUser = response.data.users.find(
          (user) => user.id === contact.id
        );
        if (activeUser) {
          // Status und benutzerdefinierte Nachricht aktualisieren
          contact.user_status = activeUser.user_status;
          contact.custom_status_message = activeUser.custom_status_message;
          contact.online = activeUser.user_status !== "offline";
        }
      });

      // Bei ausgewähltem Kontakt auch diesen aktualisieren
      if (selectedContact.value) {
        const activeUser = response.data.users.find(
          (user) => user.id === selectedContact.value.id
        );
        if (activeUser) {
          selectedContact.value.user_status = activeUser.user_status;
          selectedContact.value.custom_status_message =
            activeUser.custom_status_message;
          selectedContact.value.online = activeUser.user_status !== "offline";
        }
      }
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Kontaktstatus:", error);
  }
};

// Kontakte/Chats laden
const fetchContacts = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}chat.php?type=contacts`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      contacts.value = response.data.contacts.map((contact) => ({
        ...contact,
        // Fallback-Avatar-Farbe basierend auf ID
        avatarColor: contact.avatar ? null : getColorFromString(contact.name),
      }));
    }
  } catch (error) {
    console.error("Fehler beim Laden der Kontakte:", error);
    toast.error("Kontakte konnten nicht geladen werden.");
  }
};

// Alle Benutzer für die Chat-Erstellung laden
const fetchAllUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}chat.php?type=users`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      // Aktuellen Benutzer ausschließen
      allUsers.value = response.data.users
        .filter((user) => user.id !== currentUser.value.id)
        .map((user) => ({
          ...user,
          name: `${user.vorname} ${user.nachname}`,
          avatarColor: getColorFromString(`${user.vorname} ${user.nachname}`),
        }));
    }
  } catch (error) {
    console.error("Fehler beim Laden der Benutzer:", error);
  }
};

// Nachrichten für einen Kontakt laden
const fetchMessages = async (contact) => {
  if (!contact) return;

  loading.value = true;
  try {
    // Benutzerliste laden, wenn es sich um einen Gruppenchat handelt
    if (contact.type === "group") {
      await fetchAllUsers(); // Sicherstellen, dass alle Benutzer geladen sind für Gruppenchats
    }
    const response = await axios.get(
      `${API_BASE_URL}chat.php?recipient_id=${contact.id}&recipient_type=${contact.type}`,
      {
        withCredentials: true,
      }
    );

    if (response.data.status === "success") {
      messages.value = response.data.messages.reverse();

      // Als gelesen markieren
      if (contact.unreadCount) {
        await markAsRead(contact.id, contact.type);

        // Aktualisiere den ungelesenen Zähler im UI
        const contactIndex = contacts.value.findIndex(
          (c) => c.id === contact.id && c.type === contact.type
        );
        if (contactIndex !== -1) {
          contacts.value[contactIndex].unreadCount = 0;
          contacts.value[contactIndex].unread = false;
        }
      }

      // Chat-Benachrichtigungen zurücksetzen
      const chatId =
        contact.type === "user" ? contact.id : `${contact.type}_${contact.id}`;
      ChatNotificationService.markAsRead(chatId);

      // Scrolle zum Ende der Nachrichtenliste mit verzögertem Scroll
      setTimeout(() => {
        scrollToBottom();
      }, 300); // Längere Verzögerung für bessere Zuverlässigkeit
    }
  } catch (error) {
    console.error("Fehler beim Laden der Nachrichten:", error);
    toast.error("Nachrichten konnten nicht geladen werden.");
  } finally {
    loading.value = false;
  }
};

// Hilfsfunktionen für Status-Anzeige
const getStatusColor = (status) => {
  switch (status) {
    case "online":
      return "success";
    case "away":
      return "amber";
    case "busy":
      return "error";
    case "custom":
      return "info";
    case "mobile":
      return "purple";
    case "offline":
    default:
      return "grey";
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "online":
      return "Online";
    case "away":
      return "Abwesend";
    case "busy":
      return "Beschäftigt";
    case "mobile":
      return "Mobil";
    case "custom":
      return "Benutzerdefiniert";
    case "offline":
    default:
      return "Offline";
  }
};

// Nachricht als gelesen markieren
const markAsRead = async (recipientId, recipientType) => {
  try {
    // API-Aufruf wie bisher
    const response = await axios.post(
      `${API_BASE_URL}chat.php?action=mark_read`,
      {
        recipient_id: recipientId,
        recipient_type: recipientType,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      // Lokale UI aktualisieren (bisheriger Code)
      if (selectedContact.value) {
        selectedContact.value.unread = false;
      }

      // NEU: WebSocket-Nachricht senden, um den Sender zu informieren
      if (WebSocketService.isConnected.value) {
        WebSocketService.send({
          type: "message_read",
          sender_id: currentUser.value.id,
          recipient_id: recipientId,
          recipient_type: recipientType,
          read_at: new Date().toISOString(),
        });
      }
    }
  } catch (error) {
    console.error("Fehler beim Markieren als gelesen:", error);
  }
};

// Nachricht senden
const sendMessage = async () => {
  if (!newMessage.value.trim() || !selectedContact.value) return;

  try {
    const messageId = "temp_" + Date.now();

    const messageData = {
      type: "chat_message",
      recipient_id: selectedContact.value.id,
      recipient_type: selectedContact.value.type || "user",
      message: newMessage.value.trim(),
      message_uuid: messageId,
      // Sende weitere wichtige Metadaten mit
      sender_id: currentUser.value.id,
      sender_vorname: currentUser.value.vorname,
      sender_nachname: currentUser.value.nachname,
      created_at: new Date().toISOString(),
    };

    // Lokale temporäre ID für die Nachricht
    const tempId = messageId;

    // Lokale Nachricht hinzufügen (mit vorläufiger ID)
    const tempMessage = {
      id: tempId,
      _temp: true,
      sender_id: currentUser.value.id,
      recipient_id: selectedContact.value.id,
      recipient_type: selectedContact.value.type || "user",
      message: newMessage.value.trim(),
      created_at: new Date().toISOString(),
      // Füge Absenderinfos hinzu für bessere Anzeige
      sender_vorname: currentUser.value.vorname,
      sender_nachname: currentUser.value.nachname,
    };

    // Zur Nachrichtenliste hinzufügen
    messages.value.push(tempMessage);

    // Eingabefeld zurücksetzen
    newMessage.value = "";

    // Scrolle zum Ende der Nachrichtenliste
    await nextTick();
    scrollToBottom();

    // Überprüfung, ob WebSocket verbunden ist
    if (!WebSocketService.isConnected.value) {
      // Token-Logik hier...
    }

    // WebSocket-Verbindung verwenden, wenn verfügbar
    if (WebSocketService.isConnected.value) {
      const success = WebSocketService.send(messageData);

      if (!success) {
        throw new Error("WebSocket.send fehlgeschlagen");
      }
    } else {
      // API-Fallback, wenn WebSocket nicht verfügbar
      const response = await axios.post(
        `${API_BASE_URL}chat.php`,
        messageData,
        { withCredentials: true }
      );

      if (response.data.status === "success") {
        // Ersetze temporäre ID durch echte ID
        const index = messages.value.findIndex((m) => m.id === tempId);
        if (index !== -1) {
          messages.value[index].id = response.data.message_id;
          messages.value[index]._temp = false;
        }
      } else {
        throw new Error("API-Fehler: " + response.data.message);
      }
    }
  } catch (error) {
    console.error("Fehler beim Senden der Nachricht:", error);
    toast.error("Nachricht konnte nicht gesendet werden.");

    // Entferne die temporäre Nachricht bei Fehler
    messages.value = messages.value.filter((m) => !m._temp);
  }
};

// Automatisches Scrollen zum Ende der Nachrichtenliste
const scrollToBottom = () => {
  setTimeout(() => {
    try {
      // Methode 1: Direkte Referenz über DOM query
      const messagesDiv = document.querySelector(".chat-messages");
      if (messagesDiv) {
        messagesDiv.scrollTop = messagesDiv.scrollHeight;
        return;
      }

      // Methode 2: Über ref (falls Methode 1 fehlschlägt)
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop =
          messagesContainer.value.scrollHeight;
        return;
      }

      // Methode 3: Letztes Element in den Fokus setzen
      const messages = document.querySelectorAll(".message-item");
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        lastMessage.scrollIntoView({ behavior: "auto", block: "end" });
        return;
      }

      console.warn("Keine der Scroll-Methoden war erfolgreich");
    } catch (error) {
      console.error("Fehler beim Scrollen:", error);
    }
  }, 300); // Längere Verzögerung für bessere Kompatibilität
};

// Verarbeite eingehende Tipp-Status
const handleTypingStatus = (data) => {
  // Nur verarbeiten, wenn der Absender der aktuell ausgewählte Kontakt ist
  if (
    selectedContact.value &&
    data.sender_id == selectedContact.value.id &&
    data.recipient_id == currentUser.value.id
  ) {
    // isTyping-Status setzen
    selectedContact.value.isTyping = data.is_typing;

    // Nach 5 Sekunden automatisch zurücksetzen (Fallback)
    if (data.is_typing) {
      setTimeout(() => {
        if (selectedContact.value) {
          selectedContact.value.isTyping = false;
        }
      }, 5000);
    }
  }
};

// WebSocket-Verbindung einrichten
const setupWebSocket = () => {
  // Erst alle vorherigen Handler entfernen, falls vorhanden
  WebSocketService.removeMessageHandler("chat_message", handleWebSocketMessage);
  WebSocketService.removeMessageHandler("message_read", handleWebSocketMessage);
  WebSocketService.removeMessageHandler("typing_status", handleTypingStatus);
  WebSocketService.removeMessageHandler(
    "user_status_update",
    handleStatusUpdate
  );

  // Dann die Handler neu registrieren
  WebSocketService.addMessageHandler("chat_message", handleWebSocketMessage);
  WebSocketService.addMessageHandler("message_read", handleWebSocketMessage);
  WebSocketService.addMessageHandler("typing_status", handleTypingStatus);
  WebSocketService.addMessageHandler("user_status_update", handleStatusUpdate);

  // Sicherstellen, dass der WebSocket verbunden ist (Cookie-Auth am /ws-Proxy)
  if (!WebSocketService.isConnected.value) {
    WebSocketService.connect();
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Füge diese neue Handler-Funktion hinzu:
const handleStatusUpdate = (data) => {
  // Prüfen, ob die User-Daten vollständig sind
  if (data && data.user && data.user.id) {
    const updatedUser = data.user;

    // Aktualisiere den Status in der Kontaktliste
    contacts.value.forEach((contact) => {
      if (contact.type === "user" && contact.id === updatedUser.id) {
        // Status aktualisieren
        contact.user_status = updatedUser.status;
        contact.custom_status_message = updatedUser.custom_status_message;

        // Wenn der Kontakt gerade ausgewählt ist, auch dort den Status aktualisieren
        if (selectedContact.value && selectedContact.value.id === contact.id) {
          selectedContact.value.user_status = updatedUser.status;
          selectedContact.value.custom_status_message =
            updatedUser.custom_status_message;
        }

      }
    });
  }
};

const setupScrollObserver = () => {
  // Mit nextTick warten bis DOM aktualisiert ist
  nextTick(() => {
    if (!window.MutationObserver) {
      console.warn(
        "MutationObserver wird von diesem Browser nicht unterstützt"
      );
      return;
    }

    // Mehrere Versuche, den Container zu finden
    const targetNode =
      document.querySelector(".chat-messages") ||
      document.querySelector(".chat-card") ||
      document.querySelector(".chat-main");

    if (!targetNode) {
      console.warn(
        "Chat-Container für Observer nicht gefunden, erneuter Versuch in 1s"
      );
      // Verzögerter erneuter Versuch
      setTimeout(setupScrollObserver, 1000);
      return;
    }

    const config = { childList: true, subtree: true };

    const callback = function (mutationsList, observer) {
      // Wenn Nachrichten hinzugefügt wurden, nach unten scrollen
      for (const mutation of mutationsList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          scrollToBottom();
        }
      }
    };

    try {
      const observer = new MutationObserver(callback);
      observer.observe(targetNode, config);

      // Speichern für spätere Bereinigung
      scrollObserver.value = observer;
    } catch (error) {
      console.error("Fehler beim Einrichten des MutationObserver:", error);
    }
  });
};

// WebSocket-Nachrichten verarbeiten
const handleWebSocketMessage = (data) => {
  try {

    // Chat-Nachricht verarbeiten
    if (data.type === "chat_message") {
      // Prüfen, ob die Nachricht für den aktuellen Chat ist
      const isSender = data.sender_id == currentUser.value.id;
      const isForCurrentChat =
        selectedContact.value &&
        ((selectedContact.value.type === "user" &&
          ((data.sender_id == selectedContact.value.id &&
            data.recipient_id == currentUser.value.id) ||
            (data.recipient_id == selectedContact.value.id &&
              data.sender_id == currentUser.value.id))) ||
          (selectedContact.value.type === "group" &&
            data.recipient_id == selectedContact.value.id));

      // An die Kontaktliste weitergeben
      updateContactWithNewMessage(data);

      // WICHTIG: Auch bei Selbstsendung dem aktuellen Chat hinzufügen
      // (Entferne die !isSender Bedingung, damit alle Nachrichten angezeigt werden)
      if (isForCurrentChat) {
        // Prüfen, ob die Nachricht bereits vorhanden ist
        const isDuplicate = messages.value.some(
          (msg) =>
            (msg.id === data.id && msg.id !== undefined) ||
            (msg.message === data.message &&
              msg.sender_id === data.sender_id &&
              Math.abs(
                new Date(msg.created_at || Date.now()) -
                  new Date(data.created_at || Date.now())
              ) < 5000)
        );

        if (!isDuplicate) {
          // Nachricht zur Liste hinzufügen
          messages.value.push(data);

          // Zum Ende scrollen
          nextTick(() => {
            scrollToBottom();
          });

          // Als gelesen markieren wenn ich nicht der Sender bin
          if (!isSender) {
            markAsRead(data.sender_id, "user");
          }
        } else {
        }
      } else {
      }

      // Benachrichtigung senden, wenn nicht im aktiven Chat
      if (!isForCurrentChat && !isSender) {
        ChatNotificationService.newMessage(data, true);
      }
    }

    // Neu: Lesebestätigungen verarbeiten
    if (data.type === "message_read") {

      // Prüfe, ob die Lesebestätigung für den aktuell ausgewählten Chat gilt
      if (
        selectedContact.value &&
        ((selectedContact.value.type === "user" &&
          data.sender_id == selectedContact.value.id) ||
          (selectedContact.value.type === "group" &&
            data.recipient_id == selectedContact.value.id))
      ) {
        // Alle Nachrichten als gelesen markieren, die von mir an diesen Kontakt gesendet wurden
        messages.value.forEach((msg) => {
          if (msg.sender_id === currentUser.value.id && !msg.read_status) {
            msg.read_status = 1;
          }
        });

      }
    }
  } catch (error) {
    console.error("Fehler beim Verarbeiten der WebSocket-Nachricht:", error);
  }
};

// Am Anfang des Skripts einen Set für verarbeitete Nachrichten hinzufügen
const processedMessages = new Set();

// Optional: Regelmäßig alte Nachrichten-IDs aus dem Set entfernen
setInterval(() => {
  // Alte Einträge entfernen (älter als 1 Minute)
  if (processedMessages.size > 100) {
    processedMessages.clear();
  }
}, 60000);

// Kontakt mit neuer Nachricht aktualisieren
// Kontakt mit neuer Nachricht aktualisieren
const updateContactWithNewMessage = (message) => {

  const isSender = message.sender_id == currentUser.value.id;
  let contactId, contactType;

  if (message.recipient_type === "user") {
    // Bei Direktnachrichten: Der andere Benutzer ist der Kontakt
    contactId = isSender ? message.recipient_id : message.sender_id;
    contactType = "user";
  } else {
    // Bei Gruppen: Die Gruppe selbst ist der Kontakt
    contactId = message.recipient_id;
    contactType = message.recipient_type;
  }

  // Prüfe, ob der Kontakt bereits in der Liste ist
  const contactIndex = contacts.value.findIndex(
    (c) => c.id == contactId && c.type === contactType
  );

  if (contactIndex !== -1) {

    // Kontakt aktualisieren
    contacts.value[contactIndex].lastMessage = message.message;
    contacts.value[contactIndex].timestamp =
      message.timestamp || new Date().toISOString();

    // Nur als ungelesen markieren, wenn:
    // 1. Wir nicht der Absender sind UND
    // 2. Es nicht der aktive Chat ist ODER ein anderer Nutzer in der gleichen Gruppe schreibt
    const isActiveChat =
      selectedContact.value &&
      selectedContact.value.id == contactId &&
      selectedContact.value.type === contactType;

    if (!isSender && (!isActiveChat || message.recipient_type === "group")) {
      contacts.value[contactIndex].unread = true;
      contacts.value[contactIndex].unreadCount =
        (contacts.value[contactIndex].unreadCount || 0) + 1;
    }

    // Nach dem Aktualisieren neu sortieren - wichtig für UI-Updates!
    sortContacts();
  } else if (!isSender) {
    // Neuen Kontakt nur hinzufügen, wenn wir nicht der Absender sind
    fetchContacts();
  }
};

// Kontakte sortieren (muss explizit als Reaktiv-Trigger funktionieren)
const sortContacts = () => {
  // Reaktivität erzwingen, indem wir eine neue Kopie erstellen
  const sorted = [...contacts.value].sort((a, b) => {
    // Ungelesene zuerst
    if (a.unread && !b.unread) return -1;
    if (!a.unread && b.unread) return 1;

    // Dann nach Zeitstempel
    const dateA = new Date(a.timestamp || 0);
    const dateB = new Date(b.timestamp || 0);
    return dateB - dateA;
  });

  contacts.value = sorted;
};

// Kontaktliste aktualisieren, wenn neue Nachrichten eintreffen
const updateContactsList = (data) => {
  let contactId,
    contactType,
    isUnread = false;

  // Bestimme den Kontakt basierend auf der Nachrichtenrichtung
  if (data.sender_id === currentUser.value.id) {
    // Vom aktuellen Benutzer gesendete Nachricht
    contactId = data.recipient_id;
    contactType = data.recipient_type;
  } else {
    // Empfangene Nachricht
    contactId = data.sender_id;
    contactType = "user"; // Direktnachrichten sind immer vom Typ 'user'
    isUnread = true; // Empfangene Nachrichten sind ungelesen
  }

  // Für Gruppen-Nachrichten
  if (data.recipient_type === "group") {
    contactId = data.recipient_id;
    contactType = "group";
    isUnread = data.sender_id !== currentUser.value.id;
  }

  // Broadcast-Nachrichten
  if (data.recipient_type === "all") {
    contactId = 0; // Spezielle ID für Broadcast
    contactType = "all";
    isUnread = data.sender_id !== currentUser.value.id;
  }

  // Kontakt in der Liste finden oder erstellen
  const contactIndex = contacts.value.findIndex(
    (c) => c.id === contactId && c.type === contactType
  );

  if (contactIndex !== -1) {
    // Kontakt existiert bereits, aktualisiere die letzte Nachricht
    contacts.value[contactIndex].lastMessage = data.message;
    contacts.value[contactIndex].timestamp = data.timestamp;

    // Ungelesenen Zähler aktualisieren
    if (
      isUnread &&
      (!selectedContact.value ||
        selectedContact.value.id !== contactId ||
        selectedContact.value.type !== contactType)
    ) {
      contacts.value[contactIndex].unreadCount =
        (contacts.value[contactIndex].unreadCount || 0) + 1;
      contacts.value[contactIndex].unread = true;
    }
  } else {
    // Neuen Kontakt erstellen
    fetchContacts(); // Einfacher: Kontaktliste neu laden
  }
};

// Logik für mobile Ansicht hinzufügen
const showSidebar = ref(true);

// Computed property zur Steuerung der Sidebar-Anzeige auf Mobilgeräten
const sidebarVisible = computed(() => {
  if (isMobile.value) {
    return !selectedContact.value && showSidebar.value;
  }
  return true;
});

// Zurück-Funktion für mobile Ansicht
const goBackToContacts = () => {
  selectedContact.value = null;
  showSidebar.value = true;
};

// Funktion zur Kontaktauswahl erweitern
const selectContact = async (contact) => {
  if (!contact) return;

  selectedContact.value = contact;
  showNewChatDialog.value = false;

  // Auf Mobilgeräten die Sidebar ausblenden
  if (isMobile.value) {
    showSidebar.value = false;
  }

  await fetchMessages(contact);

  // Nachrichten als gelesen markieren
  if (contact.unread) {
    markAsRead(contact.id, contact.type || "user");
    contact.unread = false;
  }

  // Scrolle zum Ende der Nachrichtenliste
  nextTick(() => {
    scrollToBottom();
  });
};

const showStatusDialog = ref(false);
const openStatusMenu = () => {
  showStatusDialog.value = true;
};

// Neuen Chat starten
const startChat = (user) => {
  // Prüfen, ob der Chat bereits existiert
  const existingChat = contacts.value.find(
    (c) => c.type === "user" && c.id === user.id
  );

  if (existingChat) {
    // Existierenden Chat öffnen
    selectContact(existingChat);
    showNewChatDialog.value = false;
    return;
  }

  // Neuen Chat in der Kontaktliste erstellen
  const newContact = {
    id: user.id,
    name: user.name,
    type: "user",
    avatar: user.avatar,
    avatarColor: user.avatarColor || getColorFromString(user.name),
    unreadCount: 0,
    unread: false,
    online: user.user_status === "online",
  };

  contacts.value.push(newContact);
  selectContact(newContact);
  showNewChatDialog.value = false;
};

// Neue Gruppe erstellen
const createChat = async () => {
  if (newChatTab.value === "user") {
    // Direkten Chat erstellen
    if (filteredUsers.value.length > 0) {
      startChat(filteredUsers.value[0]);
    }
  } else {
    // Gruppe erstellen - mit besserer Validierung
    if (!newGroup.value.name || newGroup.value.name.trim() === "") {
      toast.error("Bitte gib einen Gruppennamen ein.");
      return;
    }

    if (!newGroup.value.members || newGroup.value.members.length === 0) {
      toast.error("Bitte wähle mindestens ein Mitglied aus.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}chat.php?action=create_group`,
        {
          name: newGroup.value.name.trim(),
          description: newGroup.value.description?.trim() || "",
          members: newGroup.value.members,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.status === "success") {
        toast.success("Gruppe erfolgreich erstellt");
        showNewChatDialog.value = false;

        // Neue Gruppe zur Liste hinzufügen
        const newGroupData = {
          id: response.data.group_id,
          name: response.data.group_name,
          type: "group",
          avatarColor: getColorFromString(response.data.group_name),
          unreadCount: 0,
          unread: false,
        };

        contacts.value.push(newGroupData);
        selectContact(newGroupData);

        // Formular zurücksetzen
        resetGroupForm();
      }
    } catch (error) {
      console.error("Fehler beim Erstellen der Gruppe:", error);

      // Verbesserte Fehlerbehandlung
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(`Fehler: ${error.response.data.message}`);
      } else {
        toast.error("Gruppe konnte nicht erstellt werden.");
      }
    }
  }
};

// Gruppenchat-Formular zurücksetzen
const resetGroupForm = () => {
  newGroup.value = {
    name: "",
    description: "",
    members: [],
  };
};

// Nachrichtensuche
const searchMessages = async () => {
  if (!globalSearchQuery.value.trim()) return;

  hasSearched.value = true;
  loading.value = true;
  searchResults.value = []; // Ergebnisse zurücksetzen

  try {
    const response = await axios.get(
      `${API_BASE_URL}chat.php?action=search&query=${encodeURIComponent(
        globalSearchQuery.value
      )}`,
      {
        withCredentials: true,
      }
    );

    if (response.data.status === "success") {
      // Die API gibt die Ergebnisse unter "messages" zurück, nicht "results"
      const searchData = response.data.messages || response.data.results || [];

      searchResults.value = searchData
        .filter((result) => {
          // Prüfen, ob die Nachricht den Suchbegriff enthält (als Fallback-Sicherheitsprüfung)
          return result.message
            .toLowerCase()
            .includes(globalSearchQuery.value.toLowerCase());
        })
        .map((result) => ({
          ...result,
          id: result.id,
          message: result.message,
          timestamp: result.created_at,
          contact_id:
            result.sender_id === currentUser.value.id
              ? result.recipient_id
              : result.sender_id,
          contact_type: "user",
          contact_name:
            result.sender_id === currentUser.value.id
              ? `${result.recipient_vorname} ${result.recipient_nachname}`
              : `${result.sender_vorname} ${result.sender_nachname}`,
          contact: {
            name:
              result.sender_id === currentUser.value.id
                ? `${result.recipient_vorname} ${result.recipient_nachname}`
                : `${result.sender_vorname} ${result.sender_nachname}`,
            avatarColor: getColorFromString(
              result.sender_id === currentUser.value.id
                ? `${result.recipient_vorname} ${result.recipient_nachname}`
                : `${result.sender_vorname} ${result.sender_nachname}`
            ),
          },
        }));
    } else {
      console.warn(
        "Keine Suchergebnisse oder unerwartetes Antwortformat:",
        response.data
      );
    }
  } catch (error) {
    console.error("Fehler bei der Nachrichtensuche:", error);
    toast.error("Suche konnte nicht durchgeführt werden.");
  } finally {
    loading.value = false;
  }
};

// Zu einem Suchergebnis navigieren
const goToSearchResult = (result) => {
  showSearchDialog.value = false;

  // Finde den entsprechenden Kontakt
  const contact = contacts.value.find(
    (c) => c.id === result.contact_id && c.type === result.contact_type
  );

  if (contact) {
    selectContact(contact);

    // Nach kurzer Verzögerung die spezifische Nachricht hervorheben
    setTimeout(() => {
      const messageElement = document.getElementById(
        `message-${result.message_id}`
      );
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
        messageElement.classList.add("highlighted-message");

        // Hervorhebung nach kurzer Zeit wieder entfernen
        setTimeout(() => {
          messageElement.classList.remove("highlighted-message");
        }, 3000);
      }
    }, 500);
  } else {
    toast.warning("Der Chat für diese Nachricht konnte nicht gefunden werden.");
  }
};

// ======== Hilfsfunktionen ========
// Zufällige Farbe aus einem String generieren (für Avatars)
const getAvatarColor = (contact) => {
  if (!contact) return "grey";
  return contact.avatarColor || "primary";
};

// Erzeugt eine Farbe basierend auf einem String
const getColorFromString = (str) => {
  if (!str) return "grey";

  const colors = [
    "primary",
    "secondary",
    "success",
    "info",
    "warning",
    "error",
    "purple",
    "indigo",
    "teal",
    "orange",
  ];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

// Initialen aus Namen generieren
const getInitials = (contact) => {
  if (!contact || !contact.name) return "?";

  const parts = contact.name.split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Text auf bestimmte Länge kürzen
const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

// Zeitstempel formatieren
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  // Heute
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // Gestern
  if (date.toDateString() === yesterday.toDateString()) {
    return "Gestern";
  }

  // Diese Woche
  if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString("de-DE", { weekday: "short" });
  }

  // Älter
  return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};

// Nachrichtenzeit formatieren
const formatMessageTime = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  return date.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Prüft, ob eine Nachricht zur vorherigen gruppiert werden soll
const isGroupedMessage = (message, index) => {
  if (index === 0) return false;

  const prevMessage = messages.value[index - 1];
  if (!prevMessage) return false;

  // Gruppiere, wenn gleicher Absender und max. 2 Minuten Abstand
  return (
    message.sender_id === prevMessage.sender_id &&
    new Date(message.created_at) - new Date(prevMessage.created_at) <
      2 * 60 * 1000
  );
};

// Absender einer Nachricht ermitteln
// Absender einer Nachricht ermitteln
const getMessageSender = (message) => {
  // 1. Wenn es der aktuelle Benutzer ist
  if (message.sender_id === currentUser.value.id) {
    return {
      name: `${currentUser.value.vorname} ${currentUser.value.nachname}`,
      avatar: null,
    };
  }

  // 2. Direkt Absenderinfos aus der Nachricht verwenden, wenn vorhanden
  if (message.sender_vorname && message.sender_nachname) {
    return {
      name: `${message.sender_vorname} ${message.sender_nachname}`,
      avatar: null,
      avatarColor: getColorFromString(
        `${message.sender_vorname} ${message.sender_nachname}`
      ),
    };
  }

  // 3. Suche in Kontakten, ignoriere den Typ für Gruppenchats
  const contact = contacts.value.find((c) => c.id === message.sender_id);
  if (contact) return contact;

  // 4. Erweiterte Suche in allUsers für Gruppenmitglieder
  const user = allUsers.value.find((u) => u.id === message.sender_id);
  if (user) {
    return {
      id: user.id,
      name: `${user.vorname} ${user.nachname}`,
      avatar: null,
      avatarColor: getColorFromString(`${user.vorname} ${user.nachname}`),
    };
  }

  // 5. Fallback mit verbesserten Informationen
  const fallbackName =
    message.sender_name ||
    (message.sender_id ? `Benutzer #${message.sender_id}` : "Unbekannt");

  return {
    name: fallbackName,
    avatar: null,
    avatarColor: getColorFromString(fallbackName),
  };
};

// Nachrichtentext formatieren (Links, Emojis etc.)
const formatMessageText = (text) => {
  if (!text) return "";

  // Links erkennen und klickbar machen
  const formattedText = linkifyHtml(text, {
    target: "_blank",
    className: "chat-link",
  });

  // HTML bereinigen, um XSS zu verhindern
  return DOMPurify.sanitize(formattedText);
};

// ======== Lebenszyklus-Hooks ========
// Füge diese Definition zu deinen anderen ref-Variablen am Anfang des <script setup> hinzu
const isMobile = ref(false); // Definition von isMobile als reaktive Variable

// Funktion zum Aktualisieren des mobilen Status
const updateMobileStatus = () => {
  isMobile.value = window.innerWidth < 960; // 960px als Breakpoint für mobile Ansicht
};

// Füge diese Funktion zu deinem onMounted-Hook hinzu
onMounted(() => {
  setupWebSocket();
  initChatApp();

  // Mobile Status beim Laden der Komponente aktualisieren
  updateMobileStatus();

  // Event-Listener für Fenstergrößenänderungen hinzufügen
  window.addEventListener("resize", updateMobileStatus);

  nextTick(() => {
    setupScrollObserver();
  });
});

// Neue Funktion: Handhabt den Fokus auf der Seite nach Tab-Wechsel oder Reaktivierung
const handlePageFocus = () => {
  // Status aktualisieren, wenn die Seite den Fokus erhält
  if (document.visibilityState === "visible") {
    // WebSocket-Verbindung überprüfen
    if (!WebSocketService.isConnected.value) {
      WebSocketService.connect();
    }

    // Status neu laden
    fetchContactsStatus();

    // Eigenen Status überprüfen und aktualisieren, falls nötig
    if (authStore.currentStatus === "offline") {
      authStore.updateUserStatus("online");
    }
  }
};

// ✅ Beide Handler kombinieren
onBeforeUnmount(() => {
  // Event-Listener für Fenstergrößenänderungen entfernen
  window.removeEventListener("resize", updateMobileStatus);
  // Neuen Event-Listener entfernen
  window.removeEventListener("focus", handlePageFocus);
  // WebSocket-Handler entfernen
  WebSocketService.removeMessageHandler("chat_message", handleWebSocketMessage);
  WebSocketService.removeMessageHandler("message_read", handleWebSocketMessage);
  WebSocketService.removeMessageHandler("typing_status", handleTypingStatus);
  WebSocketService.removeMessageHandler(
    "user_status_update",
    handleStatusUpdate
  );

  // Falls wir gerade tippen, Status auf "nicht tippend" setzen
  if (isTypingNow.value && selectedContact.value) {
    WebSocketService.send({
      type: "typing_status",
      recipient_id: selectedContact.value.id,
      recipient_type: selectedContact.value.type || "user",
      is_typing: false,
    });
  }

  // Timeout bereinigen
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
  }

  // Observer bereinigen
  if (scrollObserver.value) {
    scrollObserver.value.disconnect();
  }

  // Alternative Verbindung schließen, falls noch vorhanden
  if (chatSocket.value) {
    chatSocket.value.close();
    chatSocket.value = null;
  }
});
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
.chat-view {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.chat-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
  height: calc(100vh - 64px); /* Höhe anpassen für Header */
}

.fill-height {
  height: 100%;
}

.chat-row {
  gap: 16px; /* Wichtig! Fügt Abstand zwischen den Spalten hinzu */
}

.chat-divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 0 8px;
}

.divider-thin {
  opacity: 0.1;
}

.chat-card,
.sidebar-card,
.dialog-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
}

/* ======== Seitenleiste ======== */
.chat-sidebar {
  height: 100%;
  max-width: 400px; /* Begrenzt die maximale Breite der Sidebar */
}

.sidebar-header {
  padding: 16px 20px;
  background: linear-gradient(
    to right,
    rgba(76, 175, 80, 0.15),
    rgba(76, 175, 80, 0.05)
  );
  border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title-text {
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* ======== Hauptchat-Bereich ======== */
.chat-main {
  flex: 1;
  height: 100%;
}

.chat-header {
  padding: 16px 20px;
  background: linear-gradient(
    to right,
    rgba(76, 175, 80, 0.1),
    rgba(76, 175, 80, 0.03)
  );
  border-bottom: 1px solid rgba(76, 175, 80, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-contact-name {
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

.typing-indicator {
  color: #4caf50;
  font-style: italic;
  height: 20px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  opacity: 0.8;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.action-btn:hover {
  opacity: 1;
  transform: scale(1.05);
}

/* ======== Chat-Nachrichten ======== */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px; /* Erhöhtes Padding */
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(100vh - 220px);
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.chat-messages::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

/* ======== Chat-Eingabe ======== */
.chat-input {
  position: relative;
  bottom: 0;
  width: 100%;
  padding: 12px 16px;
  background: rgba(30, 30, 40, 0.8);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
}

.chat-input-field {
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 8px 16px;
  margin-right: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  font-size: 1rem;
}

.chat-input-field:focus-within {
  border-color: rgba(76, 175, 80, 0.4);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.send-btn {
  transition: all 0.2s ease;
}

.send-btn:hover {
  transform: translateY(-2px);
}

/* ======== Emoji-Picker ======== */
.emoji-picker {
  background: rgba(30, 30, 35, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  max-width: 320px;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
}

.emoji-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.emoji-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
}

/* ======== Kontakt-Info-Dialog ======== */
.info-header {
  background: linear-gradient(
    to right,
    rgba(76, 175, 80, 0.15),
    rgba(76, 175, 80, 0.05)
  );
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contact-info-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
}

.info-item {
  display: flex;
  align-items: center;
  margin: 8px 0;
  width: 100%;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.dialog-title {
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 500;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dialog-content {
  padding: 20px;
}

/* ======== Chat-Options-Menü ======== */
.chat-options-menu,
.context-menu {
  background: rgba(30, 30, 35, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

/* ======== Status-Dialog ======== */
.status-dialog-title {
  background: linear-gradient(
    to right,
    rgba(76, 175, 80, 0.15),
    rgba(76, 175, 80, 0.05)
  );
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 500;
}

.status-options-list {
  background: transparent !important;
}

.status-list-item {
  transition: background-color 0.2s ease;
  border-radius: 8px;
  margin: 4px 8px;
}

.status-list-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.custom-status-section {
  background: rgba(0, 0, 0, 0.2);
}

.custom-status-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.custom-status-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.custom-status-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

/* ======== Kontaktliste ======== */
.contact-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
  max-height: calc(100vh - 220px);
  background: transparent !important;
}

.contact-list::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.contact-list::-webkit-scrollbar-track {
  background: transparent;
}

.contact-list::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.contact-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

.contact-item {
  padding: 12px;
  margin-bottom: 6px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.contact-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
}

.contact-item.active {
  background: linear-gradient(
    to right,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.05)
  );
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.contact-item.unread {
  background: rgba(76, 175, 80, 0.08);
  border-left: 3px solid rgba(76, 175, 80, 0.5);
}

/* ======== Member List ======== */
.member-list {
  background: transparent !important;
  border-radius: 8px;
}

.member-item {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: background-color 0.2s ease;
}

.member-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* ======== Nachrichten-Styling ======== */
.message-item {
  display: flex;
  margin-bottom: 8px;
  max-width: 70%;
  animation: fadeInUp 0.3s ease;
}

.message-item.own-message {
  align-self: flex-end;
  margin-right: 8px;
}

.message-item.other-message {
  align-self: flex-start;
  margin-left: 8px;
}

.message-bubble {
  padding: 10px 12px;
  border-radius: 12px;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.own-message .message-bubble {
  background: linear-gradient(
    to right,
    rgba(76, 175, 80, 0.8),
    rgba(76, 175, 80, 0.6)
  );
  border-bottom-right-radius: 4px;
  color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.other-message .message-bubble {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

/* Animation für neue Nachrichten */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Eigener Status im Sidebar */
.own-status-container {
  display: flex;
  align-items: center;
  background: rgba(30, 30, 40, 0.6);
  border-radius: 8px;
  margin: 8px;
  padding: 8px 12px;
}

.user-status-details {
  margin-left: 12px;
  overflow: hidden;
}

.user-display-name {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(255, 255, 255, 0.9);
}

.user-current-status {
  font-size: 0.75rem;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-text {
  display: inline-block;
}

.status-text.online {
  color: #4caf50;
}
.status-text.away {
  color: #ffc107;
}
.status-text.busy {
  color: #f44336;
}
.status-text.custom {
  color: #2196f3;
}
.status-text.mobile {
  color: #9c27b0;
}
.status-text.offline {
  color: #9e9e9e;
}

/* Status im Kontakt-Info-Dialog */
.custom-status {
  background: rgba(76, 175, 80, 0.1);
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  border-left: 3px solid rgba(76, 175, 80, 0.6);
}

/* Status-Farben für Chip */
.custom-status-message {
  font-style: italic;
  color: #64b5f6; /* Info-Farbe */
}

/* Absendername in Nachrichtenblase */
.message-sender {
  font-size: 0.85rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
  line-height: 1.2;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.15);
  display: inline-block;
  text-transform: capitalize;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.own-message .message-sender {
  background: rgba(255, 255, 255, 0.2);
}

.other-message .message-sender {
  background: rgba(0, 0, 0, 0.2);
}

/* Zusätzlicher Abstand zwischen Sender und Nachrichtentext */
.message-text {
  font-size: 0.95rem;
  word-break: break-word;
  padding-top: 2px;
  margin-top: 2px;
}

/* Container für Zeit und Gelesen-Status */
.message-info {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  margin-top: 4px;
  opacity: 0.7;
  align-self: flex-end;
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 960px) {
  .chat-view {
    padding: 8px;
  }

  .chat-row {
    gap: 8px;
  }

  .chat-sidebar {
    max-width: none;
  }

  /* Wenn auf Mobilgeräten ein Chat ausgewählt ist, Seitenleiste ausblenden */
  .chat-sidebar.mobile-hidden {
    display: none;
  }

  /* Zurück-Button in der Chat-Header für Mobile */
  .mobile-back-btn {
    display: block !important;
    margin-right: 8px;
  }
}

@media (min-width: 961px) {
  .mobile-back-btn {
    display: none !important;
  }
}

/* Neue Styles für überarbeitete Dialoge */
.dialog-actions {
  padding: 12px 16px;
  background: rgba(30, 30, 35, 0.5);
}

.search-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

.search-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.2);
}

.search-field:focus-within :deep(.v-field__outline) {
  color: rgba(76, 175, 80, 0.5) !important;
}

.member-selection-list,
.user-search-list {
  background-color: transparent !important;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.member-select-item,
.search-user-item {
  margin-bottom: 4px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.member-select-item:hover,
.search-user-item:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  transform: translateY(-1px);
}

.chat-tabs {
  border-radius: 8px;
  overflow: hidden;
  background: rgba(30, 30, 35, 0.5) !important;
}

.chat-tabs :deep(.v-tab) {
  color: rgba(255, 255, 255, 0.7);
  min-width: 100px;
}

.chat-tabs :deep(.v-tab--selected) {
  color: #4caf50 !important;
}

.chat-tabs :deep(.v-tab:hover) {
  color: white;
}

.chat-tabs :deep(.v-tabs-slider) {
  background-color: #4caf50 !important;
}

.group-name-field:focus-within,
.group-description-field:focus-within,
.members-selection:focus-within {
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.file-input:focus-within {
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.supported-formats {
  display: flex;
  flex-wrap: wrap;
}

.timestamp {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
}

.search-result-item {
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-result-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
}

.search-result-text :deep(mark) {
  background-color: rgba(76, 175, 80, 0.3);
  color: white;
  border-radius: 2px;
  padding: 0 2px;
}

/* Animation für Dialogelemente */
.dialog-card :deep(.v-card-title),
.dialog-card :deep(.v-card-text),
.dialog-card :deep(.v-card-actions) {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
